import { Link } from "react-router-dom";
import BrandHeader from "@/components/BrandHeader";
import { PARTS } from "@/data/questions";

export default function Landing() {
  return (
    <div className="min-h-screen">
      <BrandHeader />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1611564393101-b834b74a9b48?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920)",
          }}
        />
        <div className="absolute inset-0 tt-hero-overlay" />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 py-20 sm:py-28 lg:py-36 text-white">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] bg-white/15 backdrop-blur px-3 py-1.5 rounded-full mb-6 border border-white/20">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--tt-accent)]" />
            Welcome to TravelTrucks
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter max-w-3xl">
            We are not hiring a virtual assistant.
            <br />
            <span className="text-[var(--tt-accent)]">We are inviting you into the family.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg max-w-2xl text-white/85 leading-relaxed">
            Take a quiet 20–30 minutes and walk us through who you are — your
            wants, your needs, your family, your fears, and where you are
            going. There are no right answers. The honest one is always best.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link
              to="/start"
              data-testid="get-started-btn"
              className="inline-flex items-center justify-center px-7 py-4 rounded-xl bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-hover)] text-white font-medium tracking-wide transition-colors shadow-lg shadow-black/20"
            >
              Begin Onboarding →
            </Link>
            <a
              href="#how-it-works"
              className="inline-flex items-center justify-center px-7 py-4 rounded-xl border border-white/30 hover:bg-white/10 text-white font-medium tracking-wide transition-colors"
              data-testid="how-it-works-anchor"
            >
              How it works
            </a>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="max-w-6xl mx-auto px-6 sm:px-8 py-20 sm:py-24">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-12 sm:gap-16 items-start">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-primary)] font-semibold mb-4">
              How it works
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[var(--tt-text)] leading-tight">
              Five parts.
              <br /> Fifteen questions.
              <br /> One honest conversation.
            </h2>
            <p className="mt-6 text-[var(--tt-muted)] leading-relaxed">
              You'll write a short answer to each question and rate yourself
              from 1 to 5 — not because we are grading you, but because we
              want to know how you see yourself. After you submit, our team
              reviews the responses with a culturally aware analysis.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {PARTS.map((p, i) => (
              <div
                key={p.id}
                className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-6 hover:border-[var(--tt-primary)] transition-colors"
                data-testid={`part-card-${i + 1}`}
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-3xl font-bold text-[var(--tt-primary)]">
                    0{i + 1}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--tt-muted)]">
                    {p.questions.length} questions
                  </span>
                </div>
                <h3 className="mt-3 font-display text-xl font-semibold text-[var(--tt-text)]">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-[var(--tt-muted)] leading-relaxed">
                  {p.subtitle}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Promise strip */}
        <div className="mt-20 bg-[var(--tt-secondary)] text-white rounded-2xl p-8 sm:p-12 grid md:grid-cols-3 gap-8">
          {[
            { t: "Family-first", d: "We honour your home, your hours, and your people." },
            { t: "Long-term", d: "We are building a team that grows together over years." },
            { t: "Human, not VA", d: "You are a person to us before you are a role." },
          ].map((x) => (
            <div key={x.t}>
              <div className="font-display text-xl font-semibold">{x.t}</div>
              <div className="mt-2 text-sm text-white/80 leading-relaxed">{x.d}</div>
            </div>
          ))}
        </div>

        <div className="mt-16 flex justify-center">
          <Link
            to="/start"
            data-testid="get-started-bottom-btn"
            className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-hover)] text-white font-medium tracking-wide transition-colors"
          >
            I'm ready — begin →
          </Link>
        </div>
      </section>

      <footer className="border-t border-[var(--tt-border)] py-8 text-center text-xs text-[var(--tt-muted)]">
        © {new Date().getFullYear()} Travel Trucks · Brendale QLD · Built for the family we're growing.
      </footer>
    </div>
  );
}
